import React, { useEffect } from 'react';
import {
  X,
  Database,
  FileCode2,
  Palette,
  LayoutDashboard,
  Save,
  UploadCloud,
  Eye,
  Sparkles,
  ArrowRight,
  Braces,
  ShieldCheck,
  Rocket,
  Repeat,
} from 'lucide-react';
import type { Theme, ThemeColors } from './types';

const GUIDE_DISMISSED_KEY = 'analytics_hub_designer_guide_dismissed';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-900 border-neutral-600 text-white',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900',
  },
};

const GUIDE_STEPS = [
  {
    icon: Database,
    iconBg: 'from-indigo-500 to-violet-600',
    iconColor: 'text-white',
    title: 'Create entities first',
    description:
      'Define your data in the Entities panel. Each entity is a JSON object you can bind into reports — metrics, tables, labels, and more.',
    tip: 'Click + Add entity, then bind fields from the sidebar.',
  },
  {
    icon: FileCode2,
    iconBg: 'from-orange-500 to-amber-600',
    iconColor: 'text-white',
    title: 'HTML = JSX structure only',
    description:
      'Use the HTML tab for markup and layout — like JSX, not a full page. Map live data with single-brace bindings.',
    tip: 'Example: {salesData.revenue} or {entityName.column}',
    code: '{entityName.field}',
  },
  {
    icon: Repeat,
    iconBg: 'from-amber-500 to-orange-600',
    iconColor: 'text-white',
    title: 'Loop over array data',
    description:
      'When an entity contains an array, use the JavaScript tab to render rows, cards, or list items dynamically.',
    tip: 'Add a container in HTML, then loop in JS with entity data injected at publish time.',
    codeBlock: `const list = document.getElementById('orders-list');
ordersData.rows.forEach((row) => {
  const el = document.createElement('div');
  el.className = 'row';
  el.innerHTML = \`<span>\${row.product}</span><span>\${row.revenue}</span>\`;
  list.appendChild(el);
});`,
    entityExample: `{
  "rows": [
    { "product": "Widget A", "revenue": 1200 },
    { "product": "Widget B", "revenue": 890 }
  ]
}`,
  },
  {
    icon: Palette,
    iconBg: 'from-sky-500 to-cyan-600',
    iconColor: 'text-white',
    title: 'CSS in the CSS tab',
    description:
      'Keep all styling in the dedicated CSS editor. Avoid inline styles in HTML so your report stays clean and maintainable.',
    tip: 'Use class names in HTML and style them in the CSS tab.',
  },
  {
    icon: LayoutDashboard,
    iconBg: 'from-emerald-500 to-teal-600',
    iconColor: 'text-white',
    title: 'Small, focused dashboards',
    description:
      'Analytics Hub works best for compact reports and dashboards — KPI cards, charts, and focused views rather than large apps.',
    tip: 'One clear story per report performs best.',
  },
  {
    icon: Save,
    iconBg: 'from-blue-500 to-indigo-600',
    iconColor: 'text-white',
    title: 'Save does not go live',
    description:
      'Saving stores your draft safely. Live analytics for end users stay unchanged until you explicitly publish.',
    tip: 'Iterate freely — saves never affect production.',
    badge: 'Draft safe',
    badgeClass: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  },
  {
    icon: UploadCloud,
    iconBg: 'from-violet-500 to-fuchsia-600',
    iconColor: 'text-white',
    title: 'Publish recomposes & goes live',
    description:
      'Publish transforms your report with AI, rebuilds charts, and deploys to Analytics Hub. Use View to open the live result.',
    tip: 'After publish, click View to preview the live dashboard.',
    secondaryIcon: Eye,
  },
] as const;

const WORKFLOW = [
  { label: 'Entities', icon: Database, color: 'text-indigo-400' },
  { label: 'HTML', icon: FileCode2, color: 'text-orange-400' },
  { label: 'CSS', icon: Palette, color: 'text-sky-400' },
  { label: 'JavaScript', icon: Braces, color: 'text-amber-400' },
  { label: 'Save', icon: Save, color: 'text-blue-400' },
  { label: 'Publish', icon: UploadCloud, color: 'text-violet-400' },
  { label: 'View', icon: Eye, color: 'text-emerald-400' },
];

export function shouldAutoShowDesignerGuide(isNewReport: boolean): boolean {
  if (isNewReport) return true;
  try {
    return localStorage.getItem(GUIDE_DISMISSED_KEY) !== '1';
  } catch {
    return true;
  }
}

export function markDesignerGuideDismissed(): void {
  try {
    localStorage.setItem(GUIDE_DISMISSED_KEY, '1');
  } catch {
    // ignore
  }
}

interface ReportDesignerGuideModalProps {
  theme?: Theme;
  isNewReport?: boolean;
  onClose: () => void;
}

const ReportDesignerGuideModal: React.FC<ReportDesignerGuideModalProps> = ({
  theme = 'dark',
  isNewReport = false,
  onClose,
}) => {
  const colors = THEME_CONFIG[theme];
  const codePanelClass =
    theme === 'dark'
      ? 'bg-neutral-950 text-neutral-300'
      : 'bg-slate-50 text-slate-800';
  const codeLabelClass =
    theme === 'dark' ? 'text-orange-400' : 'text-orange-600';
  const jsonLabelClass =
    theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600';

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleGotIt = () => {
    if (!isNewReport) markDesignerGuideDismissed();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/65 backdrop-blur-md"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border shadow-2xl overflow-hidden ${colors.border} ${colors.primary}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="designer-guide-title"
      >
        <div className={`flex-shrink-0 border-b ${colors.border}`}>
          <div className={`flex items-center justify-between gap-4 px-5 py-4 ${colors.primary}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/20">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400 mb-0.5">
                  Analytics Hub
                </p>
                <h2 id="designer-guide-title" className={`text-sm font-semibold ${colors.text.primary} truncate`}>
                  Report Designer Guide
                </h2>
                <p className={`text-[10px] ${colors.text.muted} mt-0.5 truncate`}>
                  Entities, JSX HTML, CSS tabs & publish workflow
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-md ${colors.hover} ${colors.text.muted} flex-shrink-0`}
              aria-label="Close guide"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className={`px-5 py-3 border-t ${colors.border} ${colors.secondary}`}>
            <p className={`text-[9px] font-medium uppercase tracking-wider ${colors.text.muted} mb-2`}>
              Recommended workflow
            </p>
            <div className="flex flex-wrap items-center gap-1">
              {WORKFLOW.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.label}>
                    <div
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border ${colors.border} ${colors.surface}`}
                    >
                      <Icon className={`w-3 h-3 ${step.color}`} />
                      <span className={`text-[10px] font-medium ${colors.text.secondary}`}>{step.label}</span>
                    </div>
                    {index < WORKFLOW.length - 1 && (
                      <ArrowRight className={`w-3 h-3 ${colors.text.muted} flex-shrink-0 opacity-50`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
          {GUIDE_STEPS.map((step) => {
            const Icon = step.icon;
            const SecondaryIcon = 'secondaryIcon' in step ? step.secondaryIcon : undefined;
            return (
              <div
                key={step.title}
                className={`flex gap-3.5 p-3.5 rounded-xl border ${colors.border} ${colors.surface} transition-colors hover:border-indigo-500/25`}
              >
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.iconBg} flex items-center justify-center flex-shrink-0 shadow-md`}
                >
                  <Icon className={`w-5 h-5 ${step.iconColor}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className={`text-xs font-semibold ${colors.text.primary}`}>{step.title}</h3>
                    {'badge' in step && step.badge && (
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${step.badgeClass}`}>
                        {step.badge}
                      </span>
                    )}
                    {SecondaryIcon && (
                      <span className="flex items-center gap-0.5 text-[9px] text-emerald-400 font-medium">
                        <SecondaryIcon className="w-3 h-3" />
                        View
                      </span>
                    )}
                  </div>
                  <p className={`text-[11px] leading-relaxed ${colors.text.muted}`}>{step.description}</p>
                  {'code' in step && step.code && (
                    <code className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/20 text-[10px] font-mono text-indigo-300">
                      <Braces className="w-3 h-3" />
                      {step.code}
                    </code>
                  )}
                  {'codeBlock' in step && step.codeBlock && (
                    <div className={`mt-3 rounded-lg border overflow-hidden ${colors.border}`}>
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 border-b ${colors.border} ${colors.secondary}`}>
                        <FileCode2 className={`w-3 h-3 ${codeLabelClass}`} />
                        <span className={`text-[9px] font-semibold uppercase tracking-wider ${codeLabelClass}`}>
                          JavaScript · loop
                        </span>
                      </div>
                      <pre
                        className={`px-3 py-2.5 text-[10px] font-mono leading-relaxed overflow-x-auto ${codePanelClass}`}
                      >
                        {step.codeBlock}
                      </pre>
                      {'entityExample' in step && step.entityExample && (
                        <>
                          <div className={`flex items-center gap-1.5 px-3 py-1.5 border-t border-b ${colors.border} ${colors.secondary}`}>
                            <Database className={`w-3 h-3 ${jsonLabelClass}`} />
                            <span className={`text-[9px] font-semibold uppercase tracking-wider ${jsonLabelClass}`}>
                              Entity JSON · ordersData
                            </span>
                          </div>
                          <pre
                            className={`px-3 py-2.5 text-[10px] font-mono leading-relaxed overflow-x-auto ${codePanelClass}`}
                          >
                            {step.entityExample}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                  {'entityExample' in step && step.entityExample && !('codeBlock' in step && step.codeBlock) && (
                    <div className="mt-2">
                      <p className={`text-[9px] font-medium uppercase tracking-wider ${colors.text.muted} mb-1`}>
                        Entity JSON example
                      </p>
                      <pre
                        className={`px-3 py-2 rounded-lg border ${colors.border} ${codePanelClass}`}
                      >
                        {step.entityExample}
                      </pre>
                    </div>
                  )}
                  <p className={`text-[10px] mt-2 flex items-center gap-1 ${colors.text.secondary}`}>
                    <Rocket className="w-3 h-3 text-violet-400 flex-shrink-0" />
                    {step.tip}
                  </p>
                </div>
              </div>
            );
          })}

          <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5`}>
            <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className={`text-[10px] leading-relaxed ${colors.text.muted}`}>
              <span className={`font-medium ${colors.text.secondary}`}>Pro tip:</span> Use the{' '}
              <span className="text-indigo-400">guide icon</span> in the toolbar anytime to reopen this walkthrough.
            </p>
          </div>
        </div>

        <div className={`flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t ${colors.border} ${colors.secondary}`}>
          <p className={`text-[10px] ${colors.text.muted}`}>
            {isNewReport
              ? 'Shown for new reports · Reopen anytime from the guide icon'
              : 'First-time walkthrough · Reopen anytime from the guide icon'}
          </p>
          <button
            type="button"
            onClick={handleGotIt}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-semibold transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Got it, let&apos;s build
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportDesignerGuideModal;
